<script>
export default {
  inheritAttrs: false,
  model: {
    event: 'update'
  },
  props: {
    value: {
      type: [Array, Number, String, Object],
      default: () => null
    },
    initialLocation: {
      type: Object,
      default: () => {
        return {
          lat: null,
          lng: null
        }
      }
    },
    initialRadius: {
      type: Number,
      default: 100
    },
    label: {
      type: String,
      default: null
    },
    labelSize: {
      type: Number,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    iconClass: {
      type: String,
      default: 'moving-mission'
    }
  },
  data() {
    return {
      temp: '',
      mapDialogVisible: false,
      googleMapLoaded: false,
      map: null,
      marker: null,
      searchInput: '',
      selectedLocation: {
        lat: null,
        lng: null
      },
      loading: false,
      autocompleteElement: null
    }
  },
  watch: {
    value: {
      handler(val) {
        this.temp = val
      },
      immediate: true
    }
  },
  mounted() {
    // Load Google Maps API script when component is mounted
    this.loadGoogleMapsScript()
  },
  methods: {
    handleChange(value) {
      this.$emit('update', value)
    },

    handleSelect() {
      this.mapDialogVisible = true

      // Initialize the map once the dialog is opened
      this.$nextTick(() => {
        this.initMap()
      })
    },

    loadGoogleMapsScript() {
      this.loading = true

      // Check if Google Maps API is already loaded
      if (window.google && window.google.maps) {
        this.googleMapLoaded = true
        this.loading = false
        return
      }

      try {
        // Create Google Maps script with regular version (not beta) for better compatibility
        const googleMapsScript = document.createElement('script')

        // Use a properly configured API key with correct domain restrictions
        // Check if we should use development or production key
        const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || ''

        googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps&v=quarterly`
        googleMapsScript.async = true
        googleMapsScript.defer = true

        // Add a global error handler for Google Maps API
        window.gm_authFailure = () => {
          console.error('Google Maps authentication error')
          this.$notify({
            type: 'warning',
            title: 'Google Maps Error',
            message:
              'There was an issue with Google Maps authentication. Map functionality may be limited.',
            duration: 10000
          })

          // Still set loaded to true so we can use the map with limitations
          this.googleMapLoaded = true
          this.loading = false
        }

        // Define a global callback function that Google will call when loaded
        window.initGoogleMaps = () => {
          console.log('Google Maps API loaded')
          this.googleMapLoaded = true
          this.loading = false
        }

        // Handle script loading errors
        googleMapsScript.onerror = (e) => {
          console.error('Failed to load Google Maps API', e)
          this.loading = false
          this.$notify({
            type: 'error',
            title: this.$t('ERROR') || 'Error',
            message:
              this.$t('FAILED_TO_LOAD_GOOGLE_MAPS') ||
              'Failed to load Google Maps'
          })
        }

        // Append the script to the document
        document.head.appendChild(googleMapsScript)
      } catch (error) {
        console.error('Error setting up Google Maps:', error)
        this.loading = false
      }
    },

    initMap() {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps API not loaded yet')
        setTimeout(() => this.initMap(), 500) // Retry after a short delay
        return
      }

      console.log('Initializing map')

      const mapElement = this.$refs.googleMap
      if (!mapElement) {
        console.error('Map container element not found')
        return
      }

      try {
        // Get initial coordinates from the value or use default
        let initialLat = this.initialLocation.lat || 21.028511
        let initialLng = this.initialLocation.lng || 105.804817

        // Use provided value if available
        if (
          this.value &&
          typeof this.value === 'object' &&
          this.value.lat &&
          this.value.lng
        ) {
          initialLat = parseFloat(this.value.lat)
          initialLng = parseFloat(this.value.lng)
          this.selectedLocation = { lat: initialLat, lng: initialLng }
        }

        console.log('Map initial position:', initialLat, initialLng)

        // Calculate and set the map container dimensions
        // Get the parent container height for full height calculation
        const dialogContainer = mapElement.closest('.map-container')
        const availableHeight = dialogContainer
          ? window.innerHeight - 180 // Approximate dialog header/footer/padding
          : window.innerHeight * 0.7 // Fallback to 70% of screen height

        // Make sure the map container has proper dimensions
        mapElement.style.height = `${availableHeight}px`
        mapElement.style.width = '100%'

        try {
          // Create map with billing error detection
          this.map = new window.google.maps.Map(mapElement, {
            center: { lat: initialLat, lng: initialLng },
            zoom: 15,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
            gestureHandling: 'cooperative'
          })

          // Wait a bit to check if the map loaded properly
          setTimeout(() => {
            const mapDiv = mapElement.querySelector('div')
            if (
              mapDiv &&
              mapDiv.innerText &&
              mapDiv.innerText.includes('BillingNotEnabled')
            ) {
              this.handleMapApiError()
            }
          }, 500)

          // Continue with normal setup
          // Add marker at initial position if coordinates exist
          if (initialLat && initialLng) {
            this.addMarker({ lat: initialLat, lng: initialLng })
          }

          // Adjust map view to show initial radius
          if (this.initialRadius) {
            const circle = new window.google.maps.Circle({
              center: { lat: initialLat, lng: initialLng },
              radius: this.initialRadius
            })
            this.map.fitBounds(circle.getBounds())
          }

          // Add click event to set marker
          this.map.addListener('click', (event) => {
            const latLng = event.latLng
            this.selectedLocation = {
              lat: latLng.lat(),
              lng: latLng.lng()
            }
            this.addMarker(this.selectedLocation)
          })

          // Initialize the search box
          this.initSearchBox()
        } catch (mapError) {
          console.error('Error creating Google Map:', mapError)

          // Check if this is a billing error
          if (mapError.message && mapError.message.includes('billing')) {
            this.handleMapApiError()
          }
          return
        }
      } catch (error) {
        console.error('Error initializing map:', error)
      }
    },

    initSearchBox() {
      if (!window.google || !window.google.maps) return

      const searchInput = this.$refs.searchInput
      if (
        !searchInput ||
        !searchInput.$el ||
        !searchInput.$el.querySelector('input')
      ) {
        console.warn('Search input element not properly initialized')
        return
      }

      try {
        const inputElement = searchInput.$el.querySelector('input')
        // Always use the standard Autocomplete — it works in all API versions
        // and the pac-container renders correctly with z-index fix below
        this.initStandardAutocomplete(inputElement)
      } catch (error) {
        console.error('Error initializing place autocomplete:', error)
      }
    },

    initStandardAutocomplete(inputElement) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputElement,
        { types: ['geocode'] }
      )

      this.map.addListener('bounds_changed', () => {
        autocomplete.setBounds(this.map.getBounds())
      })

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        if (!place.geometry || !place.geometry.location) return

        this.selectedLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        }

        this.addMarker(this.selectedLocation)

        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport)
        } else {
          this.map.setCenter(place.geometry.location)
          this.map.setZoom(17)
        }
      })
    },

    addMarker(location) {
      if (this.marker) {
        if (typeof this.marker.setMap === 'function') {
          this.marker.setMap(null)
        } else if (this.marker.map) {
          this.marker.map = null
        }
      }

      try {
        this.createStandardMarker(location)
        this.map.setCenter(location)
      } catch (error) {
        console.error('Error creating marker:', error)
      }
    },

    createStandardMarker(location) {
      try {
        this.marker = new window.google.maps.Marker({
          position: location,
          map: this.map,
          draggable: true,
          animation: window.google.maps.Animation.DROP
        })

        this.marker.addListener('dragend', () => {
          const position = this.marker.getPosition()
          this.selectedLocation = {
            lat: position.lat(),
            lng: position.lng()
          }
        })
      } catch (e) {
        console.error('Error creating marker:', e)
        // Show a notification to the user
        this.$message({
          message:
            'Map marker could not be created. Your Google Maps API key may need billing enabled.',
          type: 'warning',
          duration: 5000
        })
      }
    },

    handleMapApiError() {
      const mapElement = this.$refs.googleMap
      if (!mapElement) return

      // Show a notification
      this.$notify({
        title: 'Google Maps API Error',
        message: 'Billing needs to be enabled for the Google Maps API',
        type: 'warning',
        duration: 10000
      })
    },

    confirmLocation() {
      if (!this.selectedLocation.lat || !this.selectedLocation.lng) {
        this.$message.error(
          this.$t('PLEASE_SELECT_LOCATION') ||
            'Please select a location on the map'
        )
        return
      }

      this.$emit('update', this.selectedLocation)
      this.mapDialogVisible = false
    },

    cancelSelection() {
      if (this.autocompleteElement) {
        try {
          const container = this.autocompleteElement.parentNode
          if (container) container.remove()
          this.autocompleteElement = null
        } catch (e) {
          console.error('Error cleaning up autocomplete:', e)
        }
      }

      this.mapDialogVisible = false
    }
  }
}
</script>

<template>
  <div class="base-input-select-location">
    <div
      style="position: relative; width: 100%; display: flex; flex-wrap: wrap"
    >
      <div
        v-if="label"
        class="input-label"
        style="text-align: left; margin-bottom: 8px; width: 100%"
      >
        <label
          style="color: #333; font-weight: bold; position: relative"
          :style="'font-size: ' + (labelSize ? labelSize : 14) + 'px'"
        >
          {{ label }}
        </label>
      </div>

      <el-input
        :value="value && value.lat ? `${value.lat}, ${value.lng}` : ''"
        readonly
        v-on="$listeners"
      >
        <el-button v-loading="loading" slot="prepend" @click="handleSelect">
          <svg-icon
            v-if="iconClass"
            :icon-class="iconClass"
            style="font-size: 24px"
          />
        </el-button>
      </el-input>
    </div>

    <el-dialog
      :title="$t('SELECT_LOCATION') || 'Select Location'"
      :visible.sync="mapDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      @closed="cancelSelection"
      :fullscreen="true"
      append-to-body
    >
      <div class="map-container">
        <el-row :gutter="20">
          <!-- Search input and location display on the left (1/4 width) -->
          <el-col :span="6">
            <div class="map-search-container">
              <el-input
                ref="searchInput"
                v-model="searchInput"
                :placeholder="$t('SEARCH_PLACES') || 'Search for places...'"
                class="map-search-input"
                clearable
              >
              </el-input>
            </div>

            <!-- Selected coordinates display -->
            <!-- <div
              class="selected-coordinates"
              v-if="selectedLocation.lat && selectedLocation.lng"
            >
              <span
                >{{
                  $t('SELECTED_COORDINATES') || 'Selected Coordinates'
                }}:</span
              >
              <strong>
                {{ selectedLocation.lat.toFixed(6) }},
                {{ selectedLocation.lng.toFixed(6) }}
              </strong>
            </div> -->

            <!-- Action buttons moved to the left column -->
            <div class="location-actions">
              <el-button type="primary" @click="confirmLocation">
                {{ $t('CONFIRM') || 'Confirm' }}
              </el-button>
              <el-button @click="cancelSelection">
                {{ $t('CANCEL') || 'Cancel' }}
              </el-button>
            </div>
          </el-col>

          <!-- Map on the right (3/4 width) -->
          <el-col :span="18">
            <div ref="googleMap" class="google-map"></div>
          </el-col>
        </el-row>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss">
.base-input-select-location {
  .input-label label {
    left: initial;
    top: initial;
  }

  .el-input {
    border: 1px solid transparent !important;
    border-radius: 5px;
    margin: 0 !important;
    width: 100% !important;
    height: 42px !important;
  }

  .el-input__inner {
    background: rgba(0, 0, 0, 0.05) !important;
    border: 1px solid transparent !important;
    padding: 0 6px;
  }

  .el-input-group__prepend,
  .el-input-group__append {
    border: 1px solid transparent !important;
    background: rgba(0, 0, 0, 0.05) !important;
    padding: 0 10px;

    .svg-icon {
      width: 32px;
      height: 32px;
    }
  }

  input {
    height: 42px !important;
  }
}

.google-map {
  width: 100%;
  height: 100%;
  border-radius: 4px;
}

.map-search-container {
  margin-bottom: 30px;
  width: 100%;

  .el-input__inner {
    background: #ffffff !important;
    color: #333333 !important;
    border: 1px solid #dcdfe6 !important;
  }
}

.map-search-input {
  width: 100%;
}

/* Ensure Google Maps autocomplete dropdown appears above el-dialog */
.pac-container {
  z-index: 99999 !important;
}

.selected-coordinates {
  margin-top: 10px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #606266;

  strong {
    display: block;
    margin-top: 5px;
    word-break: break-all;
  }
}

.location-actions {
  margin-top: 20px;
  display: flex;
}
</style>
